import React, { FC, useEffect, useState } from "react";
import { styled } from "@mui/system";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, GAPS, RADII, SPACINGS } from "../../common/consts";
import Button, { ButtonVariants } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import Toggle from "../DataInput/Toggle";
import Select from "../DataInput/Select";
import Input from "../DataInput/Input";
import {
  ExportRuleBase,
  ExportRuleCondition,
  ExportRuleOperator,
  ExportSequenceRouting,
  ExportSequenceRule,
  ExportTagRule,
} from "../../types/addons";

/**
 * Editors for export tag rules and sequence routing, the same for an admin's
 * rules (everyone's exports) and a rep's own (common services/export/rules.ts
 * runs them). A rule is a few conditions on the LinkedIn profile or its
 * company, joined by all or any.
 */

const PERSON_FIELDS: Array<[string, string]> = [
  ["title", "Title"],
  ["headline", "Headline"],
  ["company", "Company name"],
  ["location", "Location"],
  ["city", "City"],
  ["state", "State"],
  ["country", "Country"],
  ["industry", "Industry"],
  ["yearsInCompany", "Years in current role"],
  ["jobStartDate", "Job start date"],
  ["emailDomain", "Email domain"],
  ["firstName", "First name"],
  ["lastName", "Last name"],
];
const COMPANY_FIELDS: Array<[string, string]> = [
  ["name", "Company name"],
  ["industry", "Company industry"],
  ["employeeCount", "Employee count"],
  ["employeeRange", "Employee range"],
  ["domain", "Company domain"],
  ["city", "Company city"],
  ["country", "Company country"],
  ["description", "Company description"],
];

const FIELD_OPTIONS = [
  ...PERSON_FIELDS.map(([f, label]) => ({ value: `person.${f}`, label: `Person: ${label}` })),
  ...COMPANY_FIELDS.map(([f, label]) => ({ value: `company.${f}`, label: `Company: ${label}` })),
];

const OPERATOR_OPTIONS: Array<{ value: ExportRuleOperator; label: string }> = [
  { value: "contains", label: "contains any of" },
  { value: "doesNotContain", label: "contains none of" },
  { value: "is", label: "is any of" },
  { value: "isNot", label: "is none of" },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" },
  { value: "greaterThan", label: "is more than" },
  { value: "lessThan", label: "is less than" },
  { value: "between", label: "is between" },
];

const NO_VALUES: ExportRuleOperator[] = ["isEmpty", "isNotEmpty"];

const fieldName = (c: ExportRuleCondition) =>
  (c.entity === "company" ? COMPANY_FIELDS : PERSON_FIELDS).find(([f]) => f === c.field)?.[1] || c.field;

const operatorName = (op: ExportRuleOperator) => OPERATOR_OPTIONS.find((o) => o.value === op)?.label || op;

/** One line a person can read: "Title contains any of VP, Director and Country is any of Israel". */
export const describeConditions = (rule: ExportRuleBase) =>
  rule.conditions
    .map((c) => `${fieldName(c)} ${operatorName(c.operator)}${NO_VALUES.includes(c.operator) ? "" : ` ${c.values.join(c.operator === "between" ? " and " : ", ")}`}`)
    .join(rule.match === "any" ? " or " : " and ");

const newId = () => Math.random().toString(36).slice(2, 10);
const newCondition = (): ExportRuleCondition => ({ entity: "person", field: "title", operator: "contains", values: [] });

const RuleBox = styled("div")`
  border: 1px solid var(--color-inputOutline, ${COLORS.inputOutline});
  border-radius: ${RADII.md};
  padding: ${SPACINGS.lg};
  display: flex;
  flex-direction: column;
  gap: ${GAPS.sm};
`;

const ConditionRow = styled("div")`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1.4fr) auto;
  gap: ${GAPS.sm};
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Line = styled("div")`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${GAPS.sm};
`;

/* Label and caption take the row; the switch stays at the right. */
const SwitchRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${GAPS.md};

  > :first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
  /* The Toggle's wrapper is a full-width flex row of its own. */
  > :last-child:not(:first-child) {
    flex: 0 0 auto;
    width: auto;
  }
`;

const Small = styled(Text)`
  margin: 0;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const Label = styled("div")`
  font-family: ${FONT_FAMILIES.regular};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-size: 0.875rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const ReadOnlyRule = styled("li")`
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

/** A comma-separated list in one field. Commits as you type, keeps your text while you type it. */
const ListInput: FC<{ value: string[]; onChange: (v: string[]) => void; label: string; placeholder?: string; disabled?: boolean }> = ({
  value,
  onChange,
  label,
  placeholder,
  disabled,
}) => {
  const [text, setText] = useState(value.join(", "));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(value.join(", "));
  }, [value, focused]);
  return (
    <Input
      fullWidth
      size="small"
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e: any) => {
        setText(e.target.value);
        onChange(
          String(e.target.value)
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        );
      }}
    />
  );
};

const ConditionsEditor: FC<{ rule: ExportRuleBase; onChange: (r: ExportRuleBase) => void }> = ({ rule, onChange }) => {
  const setCondition = (i: number, next: Partial<ExportRuleCondition>) =>
    onChange({ ...rule, conditions: rule.conditions.map((c, j) => (j === i ? { ...c, ...next } : c)) });
  return (
    <>
      <Line>
        <Small variant={TextVariant.sm}>When</Small>
        <Select
          value={rule.match}
          onChange={(e: any) => onChange({ ...rule, match: (e?.target?.value || e) === "any" ? "any" : "all" })}
          options={[
            { value: "all", label: "all of these hold" },
            { value: "any", label: "any of these holds" },
          ]}
        />
      </Line>
      {rule.conditions.map((c, i) => (
        <ConditionRow key={i}>
          <Select
            fullWidth
            label="Field"
            value={`${c.entity}.${c.field}`}
            onChange={(e: any) => {
              const [entity, field] = String(e?.target?.value || e).split(".");
              setCondition(i, { entity: entity === "company" ? "company" : "person", field });
            }}
            options={FIELD_OPTIONS}
          />
          <Select
            fullWidth
            label="Test"
            value={c.operator}
            onChange={(e: any) => setCondition(i, { operator: (e?.target?.value || e) as ExportRuleOperator })}
            options={OPERATOR_OPTIONS}
          />
          {NO_VALUES.includes(c.operator) ? (
            <span />
          ) : (
            <ListInput
              label={c.operator === "between" ? "From, to" : ["greaterThan", "lessThan"].includes(c.operator) ? "Number" : "Values, comma separated"}
              value={c.values}
              onChange={(values) => setCondition(i, { values })}
            />
          )}
          <Button
            variant={ButtonVariants.light}
            type="button"
            disabled={rule.conditions.length === 1}
            onClick={() => onChange({ ...rule, conditions: rule.conditions.filter((_, j) => j !== i) })}
          >
            Remove
          </Button>
        </ConditionRow>
      ))}
      <Line>
        <Button variant={ButtonVariants.light} type="button" onClick={() => onChange({ ...rule, conditions: [...rule.conditions, newCondition()] })}>
          Add a condition
        </Button>
      </Line>
    </>
  );
};

/* ── Tags ── */

export const TagRulesEditor: FC<{ rules: ExportTagRule[]; onChange: (rules: ExportTagRule[]) => void; crm: "outreach" | "hubspot" | null }> = ({
  rules,
  onChange,
  crm,
}) => {
  const set = (i: number, next: ExportTagRule) => onChange(rules.map((r, j) => (j === i ? next : r)));
  return (
    <>
      {rules.map((rule, i) => (
        <RuleBox key={rule.id}>
          <ConditionsEditor rule={rule} onChange={(r) => set(i, { ...rule, ...r })} />
          <ListInput
            label={crm === "hubspot" ? "Add to these static lists" : "Add these tags"}
            placeholder="Exec, LinkedIn"
            value={rule.tags}
            onChange={(tags) => set(i, { ...rule, tags })}
          />
          <Line>
            <Button variant={ButtonVariants.light} type="button" onClick={() => onChange(rules.filter((_, j) => j !== i))}>
              Delete rule
            </Button>
          </Line>
        </RuleBox>
      ))}
      <Line>
        <Button
          variant={ButtonVariants.light}
          type="button"
          onClick={() => onChange([...rules, { id: newId(), match: "all", conditions: [newCondition()], tags: [] }])}
        >
          Add a tag rule
        </Button>
      </Line>
    </>
  );
};

/* ── Sequences ── */

export interface SequenceOption {
  id: string;
  name: string;
}

const sequenceOptions = (sequences: SequenceOption[], current?: { id?: string | null; name?: string | null }) => {
  const opts = sequences.map((s) => ({ value: s.id, label: s.name }));
  // A saved sequence that is no longer listed (turned off, or not loaded yet) still shows.
  if (current?.id && !opts.some((o) => o.value === current.id)) opts.unshift({ value: current.id, label: current.name || `Sequence ${current.id}` });
  return opts;
};

export const SequenceRoutingEditor: FC<{
  routing: ExportSequenceRouting;
  onChange: (routing: ExportSequenceRouting) => void;
  sequences: SequenceOption[];
  sequencesError?: string | null;
  defaultLabel: string;
}> = ({ routing, onChange, sequences, sequencesError, defaultLabel }) => {
  const rules = routing.rules || [];
  const nameOf = (id: string) => sequences.find((s) => s.id === id)?.name || null;
  const setRule = (i: number, next: ExportSequenceRule) => onChange({ ...routing, rules: rules.map((r, j) => (j === i ? next : r)) });
  return (
    <>
      <SwitchRow>
        <div>
          <Label>Add people to a sequence when they are exported</Label>
          <Small variant={TextVariant.sm}>
            {"Sends from the exporting rep's own mailbox. Nobody already in a sequence, or who was ever in the chosen one, is added."}
          </Small>
        </div>
        <Toggle checked={!!routing.enabled} onChange={(v) => onChange({ ...routing, enabled: v })} />
      </SwitchRow>
      {sequencesError && <Small variant={TextVariant.sm}>{sequencesError}</Small>}
      {routing.enabled && (
        <>
          {rules.map((rule, i) => (
            <RuleBox key={rule.id}>
              <ConditionsEditor rule={rule} onChange={(r) => setRule(i, { ...rule, ...r })} />
              <Select
                fullWidth
                label="Then add them to"
                value={rule.sequenceId || ""}
                onChange={(e: any) => {
                  const id = String(e?.target?.value || e);
                  setRule(i, { ...rule, sequenceId: id, sequenceName: nameOf(id) || undefined });
                }}
                options={sequenceOptions(sequences, { id: rule.sequenceId, name: rule.sequenceName })}
              />
              <Line>
                <Button variant={ButtonVariants.light} type="button" onClick={() => onChange({ ...routing, rules: rules.filter((_, j) => j !== i) })}>
                  Delete rule
                </Button>
              </Line>
            </RuleBox>
          ))}
          <Line>
            <Button
              variant={ButtonVariants.light}
              type="button"
              onClick={() => onChange({ ...routing, rules: [...rules, { id: newId(), match: "all", conditions: [newCondition()], sequenceId: "" }] })}
            >
              Add a routing rule
            </Button>
          </Line>
          <Select
            fullWidth
            label={defaultLabel}
            value={routing.defaultSequenceId || ""}
            onChange={(e: any) => {
              const id = String(e?.target?.value || e);
              onChange({ ...routing, defaultSequenceId: id || null, defaultSequenceName: id ? nameOf(id) : null });
            }}
            options={[{ value: "", label: "None" }, ...sequenceOptions(sequences, { id: routing.defaultSequenceId, name: routing.defaultSequenceName })]}
          />
        </>
      )}
    </>
  );
};

/** Someone else's rules, as sentences: what the admin set, shown to reps. */
export const RulesSummary: FC<{ tagRules?: ExportTagRule[]; routing?: ExportSequenceRouting; crm: "outreach" | "hubspot" | null }> = ({ tagRules, routing, crm }) => {
  const tags = tagRules || [];
  const seq = routing?.enabled ? routing : null;
  if (!tags.length && !seq) return <Small variant={TextVariant.sm}>Your admin has not set any.</Small>;
  return (
    <ul style={{ margin: 0, paddingLeft: "1.25rem", display: "flex", flexDirection: "column", gap: 4 }}>
      {tags.map((r) => (
        <ReadOnlyRule key={`t-${r.id}`}>
          {crm === "hubspot" ? "Lists" : "Tags"} {r.tags.join(", ")} when {describeConditions(r)}
        </ReadOnlyRule>
      ))}
      {(seq?.rules || []).map((r) => (
        <ReadOnlyRule key={`s-${r.id}`}>
          Sequence {r.sequenceName || r.sequenceId} when {describeConditions(r)}
        </ReadOnlyRule>
      ))}
      {seq?.defaultSequenceId && <ReadOnlyRule>Otherwise sequence {seq.defaultSequenceName || seq.defaultSequenceId}</ReadOnlyRule>}
    </ul>
  );
};

/** Drops half-filled rules before saving, so the server's cleanup never silently eats one the person sees. */
export const incompleteRules = (tagRules: ExportTagRule[], routing?: ExportSequenceRouting): string | null => {
  const badCondition = (r: ExportRuleBase) => r.conditions.some((c) => !NO_VALUES.includes(c.operator) && !c.values.length);
  if (tagRules.some((r) => badCondition(r) || !r.tags.length)) return "Each tag rule needs a value in every condition and at least one tag.";
  if (routing?.enabled && (routing.rules || []).some((r) => badCondition(r) || !r.sequenceId)) return "Each routing rule needs a value in every condition and a sequence.";
  return null;
};
